export type HostedSentryEnvironment =
  | "development"
  | "staging"
  | "production";

const HOSTED_SENTRY_ENVIRONMENTS = new Set<HostedSentryEnvironment>([
  "development",
  "staging",
  "production",
]);

export const HOSTED_SENTRY_SMOKE_QUERY_PARAM = "__sentry_smoke";

/** Sentry project slug in org `accusys-es`; must match the project that owns the Hub DSN. */
export const HOSTED_SENTRY_PROJECT = "innovacore-ide-hub";

type ReleaseResolutionInput = {
  env?: Record<string, string | undefined>;
  gitHead?: string | null;
};

export function resolveHostedSentryEnvironment(
  rawEnvironment?: string | null,
): HostedSentryEnvironment | null {
  const normalized = rawEnvironment?.trim().toLowerCase();

  if (!normalized) {
    return null;
  }

  return HOSTED_SENTRY_ENVIRONMENTS.has(normalized as HostedSentryEnvironment)
    ? (normalized as HostedSentryEnvironment)
    : null;
}

export function resolveHostedSentryProject(
  rawProject?: string | null,
): string {
  return rawProject?.trim() || HOSTED_SENTRY_PROJECT;
}

export function shouldEnableHostedSentryBuild(
  rawEnvironment?: string | null,
): boolean {
  return resolveHostedSentryEnvironment(rawEnvironment) !== null;
}

export function resolveHostedSentryRelease({
  env = process.env,
  gitHead,
}: ReleaseResolutionInput = {}): string | null {
  const gitSha =
    env.VERCEL_GIT_COMMIT_SHA?.trim() ||
    env.GITHUB_SHA?.trim() ||
    gitHead?.trim() ||
    "";

  return gitSha ? `innovacore-ide-hub@${gitSha}` : null;
}

export function shouldEnableHostedSentrySmoke(
  environment: HostedSentryEnvironment | null,
  isDevBuild: boolean,
): boolean {
  return !isDevBuild && environment !== null && environment !== "production";
}

export function warnIfHostedSentryDsnMissing(
  rawEnvironment?: string | null,
  dsn?: string | null,
): string | null {
  if (!shouldEnableHostedSentryBuild(rawEnvironment)) {
    return null;
  }

  const trimmedDsn = dsn?.trim() ?? "";
  if (trimmedDsn) {
    return null;
  }

  return [
    "[sentry] VITE_SENTRY_DSN is missing for a hosted build",
    `(${rawEnvironment}). Browser errors from hub-web will not reach Sentry until the DSN is set in the hosting environment.`,
  ].join(" ");
}

export function shouldRunHostedSentrySmoke(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  const rawValue = params.get(HOSTED_SENTRY_SMOKE_QUERY_PARAM);

  if (!rawValue) {
    return false;
  }

  const normalized = rawValue.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
}
